import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalGetService, globalPostService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { createTransitionActionApi, updateTransitionActionApi } from '../apiService';
import { getLesseesListApi } from '../../program_planner/apiService';
import { REPORT_STATUS_OPTIONS } from '../../program_planner/constants';
import {
  ACTION_TYPE_OPTIONS, NOTIFICATION_RECIPIENT_OPTIONS, SET_FIELD_OPTIONS,
  INVOICE_STATUS_OPTIONS, SET_FIELD_VALUE_TYPE,
} from '../constants';

const SELECT_VALUE_OPTIONS = {
  report_status_records: REPORT_STATUS_OPTIONS,
  report_status_physical: REPORT_STATUS_OPTIONS,
  invoice_status: INVOICE_STATUS_OPTIONS,
};

const errorMessage = (response) => response?.data?.message || 'Unable to save the action due to a technical error.';

const OptionField = ({ label, options, value, onChange, required = true, error = '', onFocus }) => (
  <Autocomplete
    options={options}
    getOptionLabel={(option) => option?.label}
    getOptionSelected={(option, current) => option.id === current.id}
    value={value || null}
    onChange={(event, newValue) => onChange(newValue || null)}
    renderInput={(params) => (
      <TextField
        {...params}
        required={required}
        label={label}
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        error={!!error}
        helperText={error || ''}
        onFocus={onFocus}
      />
    )}
  />
);

const optionFor = (options, value) => {
  if (!value) return null;
  const id = typeof value === 'object' ? value?.id : value;
  return options.find((option) => option.id === id) || null;
};

const valueTypeFor = (field) => SET_FIELD_VALUE_TYPE[field?.id] || 'text';

const isValueEmpty = (valueType, value) => {
  if (valueType === 'select' || valueType === 'select-async') return !value;
  return !value || !String(value).trim();
};

const TransitionActionFormDialog = ({ transitionId, nextOrder, addEditAction, onToggleModal, getResponseBack, enqueueSnackbar }) => {
  const isEdit = addEditAction?.mode === 'edit';
  const action = addEditAction?.data;
  const initialField = isEdit ? optionFor(SET_FIELD_OPTIONS, action?.config?.field) : null;
  const initialValueType = valueTypeFor(initialField);
  const [data, setData] = useState({
    actionType: isEdit ? optionFor(ACTION_TYPE_OPTIONS, action?.action_type) || ACTION_TYPE_OPTIONS[0] : ACTION_TYPE_OPTIONS[0],
    recipient: isEdit ? optionFor(NOTIFICATION_RECIPIENT_OPTIONS, action?.config?.recipient) : null,
    notificationType: isEdit && action?.config?.notification_type != null ? String(action.config.notification_type) : '',
    message: isEdit ? (action?.config?.message || '') : '',
    field: initialField,
    value: isEdit && action?.config?.value != null
      ? (initialValueType === 'select' || initialValueType === 'select-async'
        ? optionFor(SELECT_VALUE_OPTIONS[initialField?.id] || [], action.config.value)
        : String(action.config.value))
      : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState({});
  const [lessees, setLessees] = useState([]);

  const valueType = valueTypeFor(data.field);

  useEffect(() => {
    if (data.field?.id !== 'lessee_id') return;
    globalGetService(getLesseesListApi()).then((response) => {
      if (checkApiStatus(response)) {
        const list = response?.data?.data?.lessees || [];
        const options = list.map((lessee) => ({ id: lessee.id, label: lessee.name || lessee.label }));
        setLessees(options);
        setData((prev) => (
          prev.field?.id === 'lessee_id' && prev.value && typeof prev.value !== 'object'
            ? { ...prev, value: optionFor(options, prev.value) }
            : prev
        ));
      }
    });
  }, [data.field?.id]);

  const updateData = (patch) => setData((prev) => ({ ...prev, ...patch }));

  const clearError = (key) => setError((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));

  const handleFieldChange = (value) => {
    updateData({ field: value, value: '' });
  };

  const handleSave = () => {
    const validationInputs = {
      actionType: data.actionType ? '' : 'Please select Action Type',
      recipient: data.actionType?.id === 'send_notification' && !data.recipient ? 'Please select Recipient' : '',
      // notificationType: data.actionType?.id === 'send_notification' && String(data.notificationType).trim() === '' ? 'Please enter Notification Type' : '',
      field: data.actionType?.id === 'set_field' && !data.field ? 'Please select Field' : '',
      value: data.actionType?.id === 'set_field' && isValueEmpty(valueType, data.value) ? 'Please enter Value' : '',
    };
    if (!Object.keys(validationInputs).every((key) => validationInputs[key] === '')) {
      setError(validationInputs);
      trackActivity('Project Management', {
        event_type: 'Add/Update Transition Action Failed',
        page_title: 'Planner Tracker Add/Update Transition Action',
        transition_id: transitionId || '',
        action_id: action?.id || '',
        error_source: 'Frontend',
        error: validationInputs,
      });
      return;
    }

    setSaving(true);
    const setFieldValue = valueType === 'select' || valueType === 'select-async'
      ? data.value?.id
      : valueType === 'date'
        ? (data.value ? moment(data.value).format(backendDateFormat) : '')
        : data.value;
    const payload = {
      action_type: data.actionType?.id,
      order: isEdit ? action?.order : nextOrder,
      ...(data.actionType?.id === 'send_notification' ? {
        config: { recipient: data.recipient?.id, notification_type: Number(data.notificationType), message: data.message?.trim() },
      } : {}),
      ...(data.actionType?.id === 'set_field' ? {
        config: { field: data.field?.id, value: setFieldValue },
      } : {}),
    };
    const request = isEdit
      ? globalPutService(updateTransitionActionApi(action?.id), payload)
      : globalPostService(createTransitionActionApi(transitionId), payload);
    request.then((response) => {
      setSaving(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(isEdit ? 'Action updated' : 'Action added', { variant: 'success' });
        getResponseBack();
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Transition Action Success`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Transition Action`,
          transition_id: transitionId || '',
          action_id: action?.id || '',
          request_body: payload,
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Transition Action Failed`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Transition Action`,
          transition_id: transitionId || '',
          action_id: action?.id || '',
          request_body: payload,
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <Dialog open={!!addEditAction?.modal} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>{isEdit ? 'Edit Action' : 'Add Action'}</Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 2 }}>
          {isEdit ? `Runs as step ${action?.order}.` : `Will run as step ${nextOrder}.`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <OptionField
              label="Action Type"
              options={ACTION_TYPE_OPTIONS}
              value={data.actionType}
              onChange={(value) => updateData({ actionType: value })}
              error={error.actionType}
              onFocus={() => clearError('actionType')}
            />
          </Grid>

          {data.actionType?.id === 'send_notification' ? (
            <>
              <Grid item xs={12}>
                <OptionField
                  label="Recipient"
                  options={NOTIFICATION_RECIPIENT_OPTIONS}
                  value={data.recipient}
                  onChange={(value) => updateData({ recipient: value })}
                  error={error.recipient}
                  onFocus={() => clearError('recipient')}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <TextField
                  fullWidth label="Notification Type" type="number" variant="outlined" size="small"
                  InputLabelProps={{ shrink: true }}
                  value={data.notificationType} onChange={(event) => updateData({ notificationType: event.target.value })}
                  error={!!error.notificationType}
                  helperText={error.notificationType || ''}
                  onFocus={() => clearError('notificationType')}
                />
              </Grid> */}
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline minRows={2} label="Message" variant="outlined" size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 100 }}
                  value={data.message} onChange={(event) => updateData({ message: event.target.value })}
                />
              </Grid>
            </>
          ) : null}

          {data.actionType?.id === 'set_field' ? (
            <>
              <Grid item xs={12}>
                <OptionField
                  label="Field"
                  options={SET_FIELD_OPTIONS}
                  value={data.field}
                  onChange={handleFieldChange}
                  error={error.field}
                  onFocus={() => clearError('field')}
                />
              </Grid>
              <Grid item xs={12}>
                {valueType === 'date' ? (
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <KeyboardDatePicker
                      fullWidth label="Value" format={fieldDateFormat} inputVariant="outlined" margin="dense"
                      InputLabelProps={{ shrink: true }}
                      value={data.value || null}
                      onChange={(date) => updateData({ value: date || '' })}
                      error={!!error.value}
                      helperText={error.value || ''}
                      onFocus={() => clearError('value')}
                    />
                  </MuiPickersUtilsProvider>
                ) : valueType === 'select' || valueType === 'select-async' ? (
                  <OptionField
                    label="Value"
                    options={valueType === 'select-async' ? lessees : (SELECT_VALUE_OPTIONS[data.field?.id] || [])}
                    value={data.value}
                    onChange={(value) => updateData({ value })}
                    error={error.value}
                    onFocus={() => clearError('value')}
                  />
                ) : (
                  <TextField
                    fullWidth label="Value" variant="outlined" size="small"
                    InputLabelProps={{ shrink: true }}
                    value={data.value} onChange={(event) => updateData({ value: event.target.value })}
                    error={!!error.value}
                    helperText={error.value || ''}
                    onFocus={() => clearError('value')}
                  />
                )}
              </Grid>
            </>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={() => onToggleModal()}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Action'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransitionActionFormDialog;
